import { zodResolver } from '@hookform/resolvers/zod';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { FORMTAGS_SUPPORT_EMAIL, FORMTAGS_SUPPORT_PHONE } from '../components/SupportWidget';
import { Alert, Box, Card, Grid2 as Grid, Link, Stack, Typography } from '@mui/material';
import { localizedOrganizationName, tr, uiDirection } from '../lib/i18n';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { ApiError } from '../api/client';
import { SLUG_KEY, useAuth } from '../auth/AuthContext';
import { Button } from '../components/button';
import { LanguageButton } from '../components/LanguageButton';
import { OrgLogo } from '../components/org-logo';
import { readLastBrand, type OrgBrand } from '../lib/org-brand';
import { createAppTheme } from '../theme';
import { PasswordField, TextField } from '../components/text-field';

const Schema = z.object({
  slug: z.string().trim().toLowerCase().min(3, tr('أدخل رمز الشركة', 'Enter the organization code')),
  username: z.string().trim().min(3, tr('أدخل اسم المستخدم', 'Enter your username')),
  password: z.string().min(1, tr('أدخل كلمة المرور', 'Enter your password')),
});
type Form = z.infer<typeof Schema>;

const lastSlug = () => {
  try {
    return localStorage.getItem(SLUG_KEY) ?? '';
  } catch {
    return '';
  }
};

const API_ORIGIN = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const brandCache = new Map<string, OrgBrand | null>();

/**
 * Company identity for the typed code, before any login on this device:
 * the same public info the customer form shows (name, logo, color). Debounced; unknown codes show nothing.
 */
function useSlugBrand(rawSlug: string): OrgBrand | null | undefined {
  const slug = rawSlug.trim().toLowerCase();
  const valid = /^[a-z0-9][a-z0-9-]{2,49}$/.test(slug);
  const [state, setState] = useState<{ slug: string; brand: OrgBrand | null } | null>(null);
  useEffect(() => {
    if (!valid) return;
    if (brandCache.has(slug)) {
      setState({ slug, brand: brandCache.get(slug)! });
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_ORIGIN}/api/v1/public/orgs/${encodeURIComponent(slug)}`, { signal: ctrl.signal });
        const d = res.ok ? ((await res.json()) as { slug: string; name: string; nameEn?: string | null; logoUrl: string | null; primaryColor?: string | null }) : null;
        const brand: OrgBrand | null = d ? { slug: d.slug, name: d.name, nameEn: d.nameEn ?? null, logoUrl: d.logoUrl, primaryColor: d.primaryColor ?? null } : null;
        if (res.ok || res.status === 404) brandCache.set(slug, brand);
        setState({ slug, brand });
      } catch {
        /* aborted or offline: keep the generic look */
      }
    }, 450);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [slug, valid]);
  if (!valid) return null;
  return state?.slug === slug ? state.brand : undefined; // undefined = still looking
}

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const [error, setError] = useState<string>();
  const initialSlug = params.get('company') ?? lastSlug();
  const { register, handleSubmit, formState, watch } = useForm<Form>({ resolver: zodResolver(Schema), defaultValues: { slug: initialSlug } });
  const slug = watch('slug');
  const typed = slug.trim().toLowerCase();
  const looked = useSlugBrand(slug);
  const last = readLastBrand();
  // fresh public info for the typed code; while it loads, the last company used here (if it is the same code)
  const brand = looked ?? (looked === undefined && last?.slug === typed ? last : null);
  const logoUrl = brand?.logoUrl ?? null;
  const brandName = brand ? localizedOrganizationName(brand) : null;
  const baseTheme = useTheme();
  const brandColor = brand?.primaryColor ?? null;
  const direction = uiDirection();
  const theme = useMemo(() => (brandColor ? createAppTheme(brandColor, direction) : baseTheme), [brandColor, direction, baseTheme]);
  const m3 = theme.palette.m3;

  const submit = handleSubmit(async (v) => {
    setError(undefined);
    try {
      await login(v.slug, v.username, v.password);
      nav((loc.state as { from?: string } | null)?.from ?? '/', { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : tr('تعذر الاتصال بالخادم', 'Unable to connect to the server'));
    }
  });

  return (
    <ThemeProvider theme={theme}>
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', md: 'center' },
        bgcolor: { xs: '#fff', md: m3.surfaceContainer },
        p: { xs: 0, md: 3 },
      }}
    >
      <Box sx={{ position: 'fixed', top: 12, insetInlineEnd: 12, zIndex: 2 }}>
        <LanguageButton />
      </Box>
      <Card
        sx={{
          width: '100%',
          maxWidth: 1040,
          border: 0,
          borderRadius: { xs: 0, md: '28px' },
          p: { xs: 3, sm: 4.5 },
          pb: { xs: 4, sm: 4.5 },
        }}
      >
        <Grid container columnSpacing={6} rowSpacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            {brand ? (
              <OrgLogo src={logoUrl} variant="rounded" sx={{ width: 48, height: 48, borderRadius: '12px', fontSize: 22 }}>
                {brandName?.[0]}
              </OrgLogo>
            ) : (
              // no company recognised yet: the FormTags mark (same file as the tab icon, so it is already cached)
              <Box component="img" src="/favicon-light.png" alt="FormTags" width={48} height={48} decoding="async" sx={{ display: 'block', objectFit: 'contain' }} />
            )}
            <Typography component="h1" sx={{ mt: 3, fontSize: { xs: '1.75rem', md: '2.25rem' }, lineHeight: 1.25, fontWeight: 400 }}>
              {tr('تسجيل الدخول', 'Sign in')}
            </Typography>
            <Typography sx={{ mt: 2, fontSize: '1rem', color: 'text.primary' }}>
              {brandName ? tr(`للمتابعة إلى لوحة ${brandName}`, `Continue to ${brandName}`) : tr('للمتابعة إلى لوحة شركتك', 'Continue to your organization dashboard')}
            </Typography>
            {brandName && (
              <Box
                sx={{
                  mt: 2.5,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  pl: 1.5,
                  pr: 0.5,
                  py: 0.5,
                  border: 1,
                  borderColor: m3.outline,
                  borderRadius: 999,
                }}
              >
                <OrgLogo src={logoUrl} sx={{ width: 22, height: 22, fontSize: 11 }}>{brandName[0]}</OrgLogo>
                <Typography variant="body2" fontWeight={500}>{brandName}</Typography>
              </Box>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Stack component="form" spacing={2.25} onSubmit={submit} noValidate sx={{ pt: { md: 9 } }}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                fullWidth
                label={tr('رمز الشركة', 'Organization code')}
                autoComplete="organization"
                autoFocus={!initialSlug}
                {...register('slug')}
                error={!!formState.errors.slug}
                helperText={formState.errors.slug?.message}
                slotProps={{ htmlInput: { dir: 'ltr' } }}
              />
              <TextField
                fullWidth
                label={tr('اسم المستخدم', 'Username')}
                autoComplete="username"
                autoFocus={!!initialSlug}
                {...register('username')}
                error={!!formState.errors.username}
                helperText={formState.errors.username?.message}
                slotProps={{ htmlInput: { dir: 'ltr' } }}
              />
              <PasswordField
                fullWidth
                label={tr('كلمة المرور', 'Password')}
                autoComplete="current-password"
                {...register('password')}
                error={!!formState.errors.password}
                helperText={formState.errors.password?.message}
              />
              <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2} sx={{ pt: 3 }}>
                <Button type="submit" variant="contained" loading={formState.isSubmitting} sx={{ flexShrink: 0, px: 3.5 }}>
                  {tr('تسجيل الدخول', 'Sign in')}
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Card>
      <Stack alignItems="center" spacing={1} sx={{ mt: 2.5, mb: 2 }}>
        <Box
          component="a"
          href="https://formtags.com"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            borderRadius: 999,
            color: 'text.secondary',
            textDecoration: 'none',
            fontSize: 12,
            transition: 'background-color .2s, color .2s',
            '&:hover': { bgcolor: 'rgba(0,0,0,.04)', color: 'text.primary' },
            '&:hover img': { transform: 'rotate(-8deg) scale(1.08)' },
          }}
        >
          <span>{tr('صُنع بواسطة', 'Created by')}</span>
          <Box component="img" src="/favicon-light.png" alt="" width={16} height={16} sx={{ display: 'block', objectFit: 'contain', transition: 'transform .25s' }} />
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', letterSpacing: '.01em' }}>FormTags</Box>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap justifyContent="center" sx={{ color: 'text.secondary' }}>
          <Typography variant="caption">{tr('دعم FormTags', 'FormTags support')}</Typography>
          <Link
            href={`mailto:${FORMTAGS_SUPPORT_EMAIL}`}
            underline="hover"
            dir="ltr"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'inherit', fontSize: 13 }}
          >
            <MailOutlineIcon sx={{ fontSize: 16 }} />
            {FORMTAGS_SUPPORT_EMAIL}
          </Link>
          {FORMTAGS_SUPPORT_PHONE && (
            <Link
              href={`tel:${FORMTAGS_SUPPORT_PHONE.replace(/\s/g, '')}`}
              underline="hover"
              dir="ltr"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'inherit', fontSize: 13 }}
            >
              <PhoneOutlinedIcon sx={{ fontSize: 16 }} />
              {FORMTAGS_SUPPORT_PHONE}
            </Link>
          )}
        </Stack>
      </Stack>
    </Box>
    </ThemeProvider>
  );
}
